<template>
    <div class="overflow-x-hidden">
        <div class="container max-w-[1440px]">
            <div class="grid grid-cols-1 lg:grid-cols-2">
                <div
                    class="col-span-1 py-8 lg:py-[64px] lg:pr-[60px] lg:pl-[44px]"
                >
                    <h1 class="h2 text-text-primary mb-6 lg:mb-8">
                        {{ $t("home.h1") }}
                    </h1>

                    <p
                        class="lg:text-[16px] lg:leading-[28px] text-[14px] leading-[24px] text-text-secondary mb-6 lg:mb-8"
                    >
                        {{ $t("home.text1") }}
                    </p>
                    <p
                        class="text-[16px] leading-[28px] text-text-primary font-bold mb-6 lg:mb-8"
                    >
                        {{ $t("home.text2") }}
                    </p>
                    <ul class="flex flex-col gap-[14px]">
                        <li
                            class="flex gap-2 items-center text-text-primary caption"
                        >
                            <img src="/icons/ic_face.svg" alt="" />
                            {{ $t("home.text3") }}
                        </li>
                        <li
                            class="flex gap-2 items-center text-text-primary caption"
                        >
                            <img src="/icons/ic_id.svg" alt="" />
                            {{ $t("home.text4") }}
                        </li>
                        <li
                            class="flex gap-2 items-center text-text-primary caption"
                        >
                            <img src="/icons/ic_document.svg" alt="" />{{
                                $t("home.text5")
                            }}
                        </li>
                        <li
                            class="flex gap-2 items-center text-text-primary caption"
                        >
                            <img src="/icons/ic_calendar-time.svg" alt="" />{{
                                $t("home.documentPointCV")
                            }}
                        </li>
                    </ul>

                    <div v-if="isLoggedIn">
                        <div
                            class="flex gap-4 items-center my-8 flex-col lg:flex-row"
                        >
                            <RouterLink
                                :to="routeByApplication"
                                class="btn btn-primary btn-md"
                                v-if="application?.id"
                                >{{
                                    $t("home.continueToExistingApplication")
                                }}</RouterLink
                            >
                            <RouterLink
                                :to="{ name: 'application-new' }"
                                class="btn btn-primary btn-md"
                                v-else
                                >{{ $t("home.continueTo1stStep") }}</RouterLink
                            >
                        </div>
                    </div>
                    <div v-else>
                        <div
                            class="flex gap-4 items-center my-6 lg:my-8 flex-col lg:flex-row"
                        >
                            <RouterLink
                                to="/register"
                                class="btn btn-primary btn-md"
                                >{{ $t("home.btn1") }}</RouterLink
                            >
                            <p class="p-small text-text-secondary">
                                {{ $t("home.btn2p") }}
                                <RouterLink to="/login" class="">{{
                                    $t("home.btn2")
                                }}</RouterLink
                                >.
                            </p>
                        </div>
                    </div>
                    <div class="bg-white p-4 rounded-md flex gap-2">
                        <img src="/icons/ic_card.svg" alt="" />
                        <p class="caption text-text-primary">
                            {{ $t("home.text7") }}
                        </p>
                    </div>
                </div>
                <div class="hidden lg:block">
                    <div class="w-[50vw] bg-black h-[100%] relative">
                        <img
                            class="h-[100%] w-auto object-cover"
                            src="/images/hp-image.webp"
                            alt=""
                        />
                        <div
                            class="absolute inset-0 bg-[linear-gradient(64deg,_rgba(0,0,0,0)_61.98%,_rgba(0,0,0,0.40)_100.17%),_linear-gradient(180deg,_rgba(0,29,119,0)_28.88%,_rgba(0,29,119,0.80)_100%)] bg-no-repeat bg-[position:-146.726px_0px] bg-[length:155.757%_100%]"
                        ></div>
                        <div
                            class="absolute bottom-8 left-8 right-8 flex gap-8"
                        >
                            <div
                                class="w-[4px] bg-text-onBrandSubtle rounded-sm"
                            ></div>
                            <p class="p text-text-onBrandSubtle max-w-[620px]">
                                <strong>Where You Explore You.</strong> An
                                American university experience in the heart of
                                Europe.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { useUserStore } from "@/stores/user";
import { mapState } from "pinia";
import Popper from "vue3-popper";
import type { ApplicationInterface } from "@/application/ApplicationInterface";
import { useApplicationStore } from "@/stores/application";

export default defineComponent({
    name: "HomeView",
    components: {
        Popper,
    },
    data() {
        return {
            isLoggedIn: false,
            application: null as ApplicationInterface | null,
        };
    },
    setup() {
        // @ts-ignore
        const { setPageTitle } = inject<{
            setPageTitle: (title: string) => void;
        }>("pageTitle");
        return { setPageTitle };
    },
    async mounted(): Promise<void> {
        const userStore = useUserStore();
        const token = await userStore.validToken();
        this.isLoggedIn = token !== null;
        this.application = await useApplicationStore().fetchOpen();
        this.setPageTitle(this.$t("titles.home"));
    },
    computed: {
        ...mapState(useUserStore, ["token"]),
        routeByApplication(): object {
            if (!this.application) {
                return { name: "application-new" };
            }

            if (this.application.reviewed_at) {
                return {
                    name: "application-review",
                    params: { id: this.application.id },
                };
            }

            if (this.application.personal_data_submitted_at) {
                return {
                    name: "application-documents",
                    params: { id: this.application.id },
                };
            }

            return {
                name: "application-personal",
                params: { id: this.application.id },
            };
        },
    },
    watch: {
        token(): void {
            this.isLoggedIn = this.token !== null;
        },
    },
});
</script>
